import { useRef, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  EnvelopeSimple,
  MapPin,
  TwitterLogo,
  PaperPlaneTilt,
  CircleNotch,
  CheckCircle,
} from "@phosphor-icons/react";
import { useContactSubmission } from "../hooks/use-contact-submission";

export function ContactPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const { status, error, submit } = useContactSubmission();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formRef.current || status === "sending") return;

    const data = new FormData(formRef.current);

    const ok = await submit({
      name: String(data.get("from_name") ?? ""),
      email: String(data.get("reply_to") ?? ""),
      organization: (data.get("company") as string) || undefined,
      message: String(data.get("message") ?? ""),
    });
    if (ok) formRef.current.reset();
  };

  return (
    <main className="pt-28 pb-20">
      <div className="mx-auto max-w-4xl px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <div className="mt-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            Get in Touch
          </h1>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 max-w-xl mx-auto">
            Have a question, partnership inquiry, or want to learn more? We'd
            love to hear from you.
          </p>
        </div>

        <div className="mt-14 grid gap-8 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 p-7 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500/10 to-primary-400/10 dark:from-primary-500/20 dark:to-primary-400/20">
              <EnvelopeSimple
                size={24}
                weight="duotone"
                className="text-primary-500 dark:text-primary-400"
              />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-slate-900 dark:text-white">
              Email
            </h3>
            <a
              href="mailto:corporate@digiits.com"
              className="mt-1 text-sm text-primary-500 dark:text-primary-400 hover:underline"
            >
              corporate@digiits.com
            </a>
          </div>

          <div className="rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 p-7 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500/10 to-primary-400/10 dark:from-primary-500/20 dark:to-primary-400/20">
              <MapPin
                size={24}
                weight="duotone"
                className="text-primary-500 dark:text-primary-400"
              />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-slate-900 dark:text-white">
              Location
            </h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Accra, Ghana
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 p-7 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500/10 to-primary-400/10 dark:from-primary-500/20 dark:to-primary-400/20">
              <TwitterLogo
                size={24}
                weight="duotone"
                className="text-primary-500 dark:text-primary-400"
              />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-slate-900 dark:text-white">
              Social
            </h3>
            <div className="mt-1 flex items-center justify-center gap-3">
              <a
                href="#"
                className="text-sm text-primary-500 dark:text-primary-400 hover:underline"
              >
                Twitter
              </a>
              <a
                href="#"
                className="text-sm text-primary-500 dark:text-primary-400 hover:underline"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="mt-14 rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 p-8 sm:p-10">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Send us a message
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Fill out the form below and we'll get back to you as soon as
            possible.
          </p>

          <form
            ref={formRef}
            className="mt-8 space-y-5"
            onSubmit={handleSubmit}
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Name
                </label>
                <input
                  type="text"
                  name="from_name"
                  required
                  placeholder="Your name"
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 text-sm text-slate-900 dark:text-slate-100 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Email
                </label>
                <input
                  type="email"
                  name="reply_to"
                  required
                  placeholder="you@company.com"
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 text-sm text-slate-900 dark:text-slate-100 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Company
              </label>
              <input
                type="text"
                name="company"
                placeholder="Your company name"
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 text-sm text-slate-900 dark:text-slate-100 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Message
              </label>
              <textarea
                rows={4}
                name="message"
                required
                placeholder="Tell us about your project management needs..."
                className="w-full resize-none rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 text-sm text-slate-900 dark:text-slate-100 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-primary-500"
              />
            </div>

            <button
              type="submit"
              disabled={status === "sending" || status === "sent"}
              className="group inline-flex items-center gap-2 rounded-xl bg-primary-500 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/25 transition-all hover:bg-primary-600 hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {status === "sending" && (
                <>
                  Sending...
                  <CircleNotch size={16} className="animate-spin" />
                </>
              )}
              {status === "sent" && (
                <>
                  Message Sent
                  <CheckCircle size={16} />
                </>
              )}
              {(status === "idle" || status === "error") && (
                <>
                  Send Message
                  <PaperPlaneTilt
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </>
              )}
            </button>

            {status === "error" && (
              <p className="text-sm text-red-500">
                {error ??
                  "Failed to send message. Please try again or email us directly."}
              </p>
            )}
          </form>
        </div>
      </div>
    </main>
  );
}
